import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import axios from 'axios';
import './css/track_layout.css';
import './css/track_card.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPlus, faMusic } from '@fortawesome/free-solid-svg-icons';
import EditableField from './editable_field';
import TrackActionSheet from './track_action_sheet';
import TrackMoreMenu from './track_more_menu';
import EditTrackInfoModal from './edit_track_info_modal';

const server = axios.create({ baseURL: '/api' });

function authHeaders() {
  const token = localStorage.getItem('walmates-token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const TracksWidget = forwardRef(({
  tracks,
  onTrackClick,
  onAddToQueue,
  appendMode = false,
  onTrackDelete,
  tags,
  playTrack,
  loadingTrackId,
  playlists = [],
  onAddToPlaylist,
  onTrackInfoUpdated,
  isTrackSkipped,
  onSkipTrack,
  onKeepTrack,
}, ref) => {
  const [menuTrackId, setMenuTrackId] = useState(null);
  const [sheetState, setSheetState] = useState(null);
  const [updatingTrackId, setUpdatingTrackId] = useState(null);
  const [editInfoState, setEditInfoState] = useState(null);
  const [savingTrackInfo, setSavingTrackInfo] = useState(false);

  const rootRef = useRef(null);

  const closeMenu = useCallback(() => setMenuTrackId(null), []);

  const openSheet = useCallback((track, view) => {
    closeMenu();
    setSheetState({ track, view });
  }, [closeMenu]);

  const closeSheet = useCallback(() => setSheetState(null), []);

  useImperativeHandle(ref, () => ({
    scrollToTrackById: (id) => {
      if (!id) return;
      const container = rootRef.current;
      if (!container) return;
      const row = container.querySelector(`[data-track-id="${id}"]`);
      if (row && row.scrollIntoView) {
        row.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    },
    openActionsForTrack: (trackOrId) => {
      const id = typeof trackOrId === 'string'
        ? trackOrId
        : (trackOrId?.video_id || trackOrId?.id);
      const t = typeof trackOrId === 'object' && trackOrId
        ? trackOrId
        : tracks.find(tt => (tt.video_id || tt.id) === id);
      if (t) {
        const container = rootRef.current;
        const row = container?.querySelector(`[data-track-id="${t.video_id}"]`);
        row?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        setMenuTrackId(t.video_id);
      }
    },
    openPlaylistForTrack: (track) => openSheet(track, 'playlists'),
    openUpdateInfoForTrack: (track) => handleUpdateInfo(track),
  }));

  useEffect(() => {
    const openFromEvent = (e) => {
      const t = e?.detail?.track;
      if (!t?.video_id) return;
      const container = rootRef.current;
      const row = container?.querySelector(`[data-track-id="${t.video_id}"]`);
      row?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      setMenuTrackId(t.video_id);
    };
    window.addEventListener('ui:openTrackSheet', openFromEvent);
    return () => window.removeEventListener('ui:openTrackSheet', openFromEvent);
  }, []);

  const handleUpdateInfo = async (track) => {
    if (!track?.video_id || updatingTrackId) return;
    setUpdatingTrackId(track.video_id);
    try {
      const res = await server.post(
        '/music/trackInfo/suggest',
        { video_id: track.video_id, title: track.title },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      closeMenu();
      setEditInfoState({
        track: res.data?.current || track,
        suggestion: res.data?.suggestion || {
          title: track.title,
          artist: track.artist,
          artwork: track.thumbnail_medium || track.thumbnail_default || '',
        },
      });
    } catch (e) {
      console.error('Could not fetch track info suggestion:', e);
    } finally {
      setUpdatingTrackId(null);
    }
  };

  const closeEditInfo = useCallback(() => {
    if (savingTrackInfo) return;
    setEditInfoState(null);
  }, [savingTrackInfo]);

  const handleSaveTrackInfo = async (payload) => {
    if (!payload?.video_id || savingTrackInfo) return;
    setSavingTrackInfo(true);
    try {
      const res = await server.post(
        '/music/trackInfo/update',
        payload,
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      onTrackInfoUpdated?.(res.data);
      setEditInfoState(null);
    } catch (e) {
      console.error('Could not save track info:', e);
    } finally {
      setSavingTrackInfo(false);
    }
  };

  const getTrackPlaylists = (trackId) => {
    return playlists
      .filter(pl => pl.trackIds?.includes(trackId))
      .map(pl => pl.name);
  };

  const dismissChrome = () => {
    if (sheetState) {
      closeSheet();
      return true;
    }
    closeMenu();
    return false;
  };

  const handleThumbClick = async (index, track) => {
    if (track.pending) return;
    if (dismissChrome()) return;
    if (appendMode && onAddToQueue) {
      onAddToQueue(track);
      return;
    }
    await onTrackClick(index, track);
  };

  const handlePlayNow = async (index, track) => {
    if (track.pending) return;
    if (dismissChrome()) return;
    await onTrackClick(index, track);
  };

  const checkTags = (track) => {
    const tagStr = track.tag?.trim() || "";
    if (!tags || tags.length === 0 || tagStr === "") return true;
    return tagStr.split(',').some(tag => tags.includes(tag.trim()));
  };

  const onTrackUpdate = (index, track, field, value) => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    fetch(`/api/music/updateTrack?user_id=${user_id}&video_id=${track.video_id}&field=${field}&value=${encodeURIComponent(value)}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    }).catch(err => console.error('Error updating track:', err));
  };

  const isNowPlaying = (track) => playTrack && playTrack.video_id === track.video_id;
  const trackIsSkipped = (track) => (
    typeof isTrackSkipped === 'function'
      ? isTrackSkipped(track)
      : false
  );

  return (
    <div ref={rootRef}>
      <ul className="track-list">
        {tracks.map((track, index) =>
          checkTags(track) ? (
            <li
              key={track.video_id || index}
              data-track-id={track.video_id}
              className={[
                'track-item',
                track.pending ? 'track-pending' : '',
                isNowPlaying(track) ? 'now-playing' : '',
                menuTrackId === track.video_id ? 'track-item--menu-open' : '',
                trackIsSkipped(track) ? 'track-item--skipped' : '',
              ].filter(Boolean).join(' ')}
            >
              <div
                className="track-image"
                onClick={() => handleThumbClick(index, track)}
                role="button"
                tabIndex={0}
                aria-label={
                  appendMode
                    ? `Add ${track.title || 'track'} to playing list`
                    : `Play ${track.title || 'track'}`
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleThumbClick(index, track);
                  }
                }}
              >
                {(track.thumbnail_medium || track.thumbnail_default) ? (
                  <img
                    src={track.thumbnail_medium || track.thumbnail_default}
                    alt={track.title || 'Track'}
                  />
                ) : (
                  <div className="track-image-placeholder">
                    <FontAwesomeIcon icon={faMusic} />
                  </div>
                )}
                <div className="track-image-dim" />
                {track.pending && <div className="track-overlay">Adding…</div>}
                {!track.pending && !isNowPlaying(track) && loadingTrackId === track.video_id && (
                  <div className="track-icon-overlay">
                    <div className="spinner" />
                  </div>
                )}
                {!track.pending && isNowPlaying(track) && (
                  <div className="track-icon-overlay waveform">
                    <div className="equalizer-bars">
                      <div /><div /><div />
                    </div>
                  </div>
                )}
                {!track.pending && !isNowPlaying(track) && loadingTrackId !== track.video_id && (
                  <div className={`track-play-btn${appendMode ? ' is-add' : ''}`} aria-hidden="true">
                    <FontAwesomeIcon icon={appendMode ? faPlus : faPlay} />
                  </div>
                )}
              </div>

              <div className="track-details">
                <div className="track-details-top">
                  {appendMode ? (
                    <div
                      className="track-details-text"
                      onClick={() => handlePlayNow(index, track)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handlePlayNow(index, track);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Play ${track.title || 'track'} now`}
                    >
                      <div
                        className="track-title-text"
                        style={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: isNowPlaying(track) ? 'var(--color-accent)' : 'var(--color-text)',
                        }}
                      >
                        {track.title || 'Untitled'}
                      </div>
                      <div
                        className="track-artist-text"
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-text-muted)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {track.artist || 'Unknown artist'}
                      </div>
                    </div>
                  ) : (
                    <div className="track-details-text">
                      <EditableField
                        value={track.title}
                        onSubmit={(newVal) => onTrackUpdate(index, track, 'title', newVal)}
                        scrollOnSelect={menuTrackId === track.video_id || sheetState?.track?.video_id === track.video_id}
                        textStyle={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: isNowPlaying(track) ? 'var(--color-accent)' : 'var(--color-text)',
                        }}
                        inputStyle={{
                          color: 'var(--color-text)',
                          background: 'var(--color-surface-secondary)',
                          borderRadius: 4,
                          outline: '1px solid var(--color-accent)',
                        }}
                      />
                      <EditableField
                        value={track.artist}
                        onSubmit={(newVal) => onTrackUpdate(index, track, 'artist', newVal)}
                        textStyle={{
                          fontSize: '0.75rem',
                          color: 'var(--color-text-muted)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        inputStyle={{
                          color: 'var(--color-text)',
                          background: 'var(--color-surface-secondary)',
                          borderRadius: 4,
                          outline: '1px solid var(--color-accent)',
                        }}
                      />
                    </div>
                  )}
                  <TrackMoreMenu
                    isOpen={menuTrackId === track.video_id}
                    onToggle={() => setMenuTrackId(prev => prev === track.video_id ? null : track.video_id)}
                    onClose={closeMenu}
                    updating={updatingTrackId === track.video_id}
                    playlists={playlists}
                    trackId={track.video_id}
                    isSkipped={trackIsSkipped(track)}
                    onAddToPlaylist={() => openSheet(track, 'playlists')}
                    onPlayNext={() => {
                      try {
                        window.dispatchEvent(new CustomEvent('queue:playnext', { detail: { track } }));
                        window.dispatchEvent(new CustomEvent('queue:preload', { detail: { track } }));
                      } catch (e) {}
                    }}
                    onSkip={() => onSkipTrack?.(track)}
                    onKeep={() => onKeepTrack?.(track)}
                    onUpdateInfo={() => handleUpdateInfo(track)}
                    onLyrics={() => openSheet(track, 'lyrics')}
                    onDelete={() => {
                      onTrackDelete(index, track);
                      closeMenu();
                    }}
                  />
                </div>
                {getTrackPlaylists(track.video_id).length > 0 && (
                  <div className="track-playlists">
                    {getTrackPlaylists(track.video_id).slice(0, 2).map(name => (
                      <span className="playlist-badge" key={name}>{name}</span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          ) : null
        )}
      </ul>

      <TrackActionSheet
        isOpen={!!sheetState}
        onClose={closeSheet}
        track={sheetState?.track}
        initialView={sheetState?.view}
        playlists={playlists}
        onAddToPlaylist={(videoId, playlistName) => {
          onAddToPlaylist(videoId, playlistName);
        }}
      />

      <EditTrackInfoModal
        open={!!editInfoState}
        track={editInfoState?.track}
        suggestion={editInfoState?.suggestion}
        saving={savingTrackInfo}
        onCancel={closeEditInfo}
        onSave={handleSaveTrackInfo}
      />
    </div>
  );
});

export default TracksWidget;
